import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, X } from "lucide-react";
import { Config, Handoffs } from "@/api/endpoints";
import { apiErrorMessage } from "@/api/client";
import { useAuthStore } from "@/store/auth";
import { toast } from "@/lib/feedback";
import { Avatar } from "@/components/ui";

interface Props {
  dealId: string;
  dealLead: string;
  onClose: () => void;
}

/** Hand a deal to a teammate, with the context they need to pick it up. */
export default function HandoffDialog({ dealId, dealLead, onClose }: Props) {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const teamQuery = useQuery({ queryKey: ["owners"], queryFn: () => Config.listOwners() });
  const team = (teamQuery.data ?? []).filter((u) => u.id !== me?.id);
  const recipient = team.find((u) => u.id === to);

  const mutation = useMutation({
    mutationFn: () => Handoffs.create(dealId, { to_user_id: to, note: note.trim() || null }),
    onSuccess: (h) => {
      void qc.invalidateQueries({ queryKey: ["handoffs"] });
      void qc.invalidateQueries({ queryKey: ["deal-handoffs", dealId] });
      void qc.invalidateQueries({ queryKey: ["deal-access", dealId] });
      toast.ok(`Sent to ${h.to_name ?? "your teammate"}. They'll see it on their Today page.`);
      onClose();
    },
    onError: (e) => toast.err(`Couldn't hand off: ${apiErrorMessage(e)}`),
  });

  return createPortal(
    <div className="fixed inset-0 z-[70] grid place-items-center p-4">
      <div className="overlay-backdrop" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="handoff-title"
        className="panel relative w-[min(520px,100%)] p-5 animate-fade-up"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 id="handoff-title" className="text-base font-semibold">
              Hand off {dealLead}
            </h2>
            <p className="text-sm text-mut-500 mt-0.5">
              They become the owner when they accept. You stay on the deal.
            </p>
          </div>
          <button className="btn btn-ghost !p-1.5" onClick={onClose} aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="mt-4 flex items-center gap-2 text-sm">
          <Avatar name={me?.name} size={26} />
          <ArrowRight className="w-4 h-4 text-accent-violet" />
          {recipient ? (
            <Avatar name={recipient.name} size={26} />
          ) : (
            <Avatar name="?" size={26} muted />
          )}
        </div>

        <div className="mt-4 space-y-3">
          <div>
            <label className="label" htmlFor="handoff-to">
              Hand to
            </label>
            <select
              id="handoff-to"
              className="input"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            >
              <option value="">Choose a teammate…</option>
              {team.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="handoff-note">
              Context
            </label>
            <textarea
              id="handoff-note"
              className="input min-h-[120px]"
              placeholder={"What's done, what's open, who decides…\nOne point per line."}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={!to || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? "Sending…" : "Send handoff"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
